import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Chip, Stack, Divider,
  CircularProgress, Alert, Tooltip, TextField, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper,
} from '@mui/material';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import EventSeatOutlinedIcon from '@mui/icons-material/EventSeatOutlined';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import SubscriptionsOutlinedIcon from '@mui/icons-material/SubscriptionsOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import SyncIcon from '@mui/icons-material/Sync';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { toast } from 'react-toastify';
import { expandTiers, computeAccessWindow, ESSENTIALS_TIER, frameworkAvailability, isFrameworkLocked, requiredTiersFor } from '../../Utils/frameworkTiers';
import { UpdateOrganization } from '../../Api/CreateOrganization';
import { GetFrameworkData } from '../../Api/Framework';
import { GetSeatStatus, GetOrgEntitlements, RevokeEntitlement, AdminResyncEntitlements, GetSubscriptionStatus } from '../../Api/Billing';
import { GetMyProfileView } from '../../Api/UpdateAdminUser';
import StandardListView from '../StandardComponents/StandardListView/StandardListView';
import StandardCollapsibleSection from '../StandardComponents/StandardCollapsibleSection/StandardCollapsibleSection';
import GrantEntitlementDialog from './GrantEntitlementDialog';
import ConfirmDialog from './ConfirmDialog';
import EntitlementsQuickFilters from './EntitlementsQuickFilters';
import '../../Components/Common/GlobalStyles.css';

const unwrap = (res) => res?.data?.data ?? res?.data ?? [];

// Only account_expiration_day (an int) is persisted, so presets are keyed by
// days — "Trial - 30 Days" and "Essentials - Monthly" share one entry. 30/90
// mirror monthly/quarterly Stripe billing cycles (the webhook sets the same
// window from the paid period on payment).
const ACCESS_PRESETS = [
  { label: 'Trial - 14 Days', days: 14 },
  { label: 'Trial - 30 Days / Essentials - Monthly', days: 30 },
  { label: 'Essentials - Quarterly', days: 90 },
  { label: 'Essentials - Annual', days: 365 },
  { label: 'Essentials - 2 Years', days: 730 },
];

const INHERITANCE_NOTE =
  'An industry Premium also grants that industry’s Advance (e.g. Cyber Security - Premium → Cyber Security - Advance); ' +
  'generic Premium grants generic Advance. No cross-track or generic↔industry inheritance.';

const SEATS_PER_BAND = 50;

/** Staff headroom before the next 50-user seat band is required. */
const computeRemainingUsersUntilNextBand = (seat) => {
  if (!seat) return null;
  const staff = seat.staff_count ?? 0;
  const included = seat.included_seats ?? SEATS_PER_BAND;
  const band = seat.seat_band ?? 0;
  const capacity = included + band * SEATS_PER_BAND;
  return Math.max(0, capacity - staff);
};

const buildActiveEntitlementTiers = (effectiveTiers) => {
  const tiers = new Set([ESSENTIALS_TIER, ...(effectiveTiers || [])]);
  const paid = [...tiers].filter((t) => t !== ESSENTIALS_TIER).sort();
  return [ESSENTIALS_TIER, ...paid];
};

const buildFrameworkAccessGroups = (rows) => {
  const byAccess = new Map();
  const locked = [];

  rows.forEach((row) => {
    if (!row.unlocked) {
      locked.push(row);
      return;
    }
    const key = row.accessTier || ESSENTIALS_TIER;
    if (!byAccess.has(key)) byAccess.set(key, []);
    byAccess.get(key).push(row);
  });

  const groups = [];
  if (byAccess.has(ESSENTIALS_TIER)) {
    groups.push({ label: ESSENTIALS_TIER, rows: byAccess.get(ESSENTIALS_TIER), isLocked: false });
    byAccess.delete(ESSENTIALS_TIER);
  }
  [...byAccess.keys()].sort().forEach((key) => {
    groups.push({ label: key, rows: byAccess.get(key), isLocked: false });
  });
  if (locked.length > 0) {
    groups.push({ label: 'Locked', rows: locked, isLocked: true });
  }
  return groups;
};

const toDateInput = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

const rowStatus = (r, now = Date.now()) => {
  if (r.status === 'revoked') return 'revoked';
  if (r.expires_at && new Date(r.expires_at).getTime() <= now) return 'expired';
  if (r.starts_at && new Date(r.starts_at).getTime() > now) return 'pending';
  return 'active';
};

const truncate = (s, n = 8) => (s ? (s.length > n ? `${s.slice(0, n)}…` : s) : '—');

const SOURCE_LABELS = { stripe: 'Stripe', manual: 'Manual', code: 'Code' };
const STATUS_LABELS = { active: 'Active', pending: 'Pending', expired: 'Expired', revoked: 'Revoked' };

const formatSourceLabel = (source) => SOURCE_LABELS[source] || source || '—';
const formatStatusLabel = (status) => STATUS_LABELS[status] || status || '—';

const formatGrantedBy = (userId, lookup) => {
  if (!userId) return '—';
  const user = lookup[userId];
  if (!user) return '—';
  const name = user.full_name || user.name || 'Unknown';
  const email = user.email || '';
  return email ? `${name} (${email})` : name;
};

export default function OrgEntitlementsPanel({ org, onOrgChanged }) {
  const [seat, setSeat] = useState(null);
  const [entitlements, setEntitlements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resyncing, setResyncing] = useState(false);

  const [accessEditing, setAccessEditing] = useState(false);
  const [accessDays, setAccessDays] = useState(14);
  const [accessStart, setAccessStart] = useState('');
  const [savingAccess, setSavingAccess] = useState(false);
  const [essentialsCustomDeal, setEssentialsCustomDeal] = useState(false);
  const [prepaidAmount, setPrepaidAmount] = useState('');
  const [prepaidCurrency, setPrepaidCurrency] = useState('sgd');
  const [prepaidPeriod, setPrepaidPeriod] = useState('year');
  const [prepaidNote, setPrepaidNote] = useState('');
  const [essentialsCreditUntil, setEssentialsCreditUntil] = useState('');

  const [activeFilters, setActiveFilters] = useState({});
  const [grantOpen, setGrantOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [frameworks, setFrameworks] = useState([]);
  const [frameworksLoading, setFrameworksLoading] = useState(false);
  const [grantedByLookup, setGrantedByLookup] = useState({});

  const load = useCallback(async () => {
    const id = org?.id?.trim();
    if (!id) return;
    setLoading(true);
    setSeat(null);
    setEntitlements([]);
    setAccessEditing(false);
    setActiveFilters({});
    setGrantedByLookup({});
    try {
      const [seatRes, entRes, subRes] = await Promise.all([
        GetSeatStatus(id).catch(() => null),
        GetOrgEntitlements(id).catch(() => null),
        GetSubscriptionStatus(id).catch(() => null),
      ]);
      setSeat(seatRes ? (seatRes.data?.data ?? seatRes.data) : null);
      setEntitlements(entRes ? unwrap(entRes) : []);
      const sub = subRes ? (subRes.data?.data ?? subRes.data) : null;
      setEssentialsCreditUntil(sub?.essentials_credit_until || '');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load organization');
    } finally {
      setLoading(false);
    }
  }, [org?.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const ids = [...new Set(entitlements.map((e) => e.created_by).filter(Boolean))];
    if (ids.length === 0) return undefined;

    let active = true;
    (async () => {
      const results = await Promise.all(ids.map(async (id) => {
        try {
          const res = await GetMyProfileView(id);
          return [id, res?.data?.data ?? res?.data ?? null];
        } catch {
          return [id, null];
        }
      }));
      if (!active) return;
      setGrantedByLookup((prev) => {
        const next = { ...prev };
        results.forEach(([id, user]) => {
          if (user) next[id] = user;
        });
        return next;
      });
    })();

    return () => { active = false; };
  }, [entitlements]);

  useEffect(() => {
    let active = true;
    setFrameworksLoading(true);
    GetFrameworkData()
      .then((res) => {
        if (!active) return;
        const rows = res?.data?.data ?? res?.data ?? [];
        setFrameworks(Array.isArray(rows) ? rows : []);
      })
      .catch(() => { if (active) setFrameworks([]); })
      .finally(() => { if (active) setFrameworksLoading(false); });
    return () => { active = false; };
  }, []);

  const purchasedTiers = useMemo(
    () => entitlements.filter((r) => rowStatus(r) === 'active').map((r) => r.tier),
    [entitlements],
  );

  const effectiveTiers = useMemo(() => expandTiers(purchasedTiers), [purchasedTiers]);

  const activeEntitlementTiers = useMemo(
    () => buildActiveEntitlementTiers(effectiveTiers),
    [effectiveTiers],
  );

  const visibleEntitlements = useMemo(() => entitlements.filter((r) => {
    if (activeFilters.source && r.source !== activeFilters.source) return false;
    if (activeFilters.status && rowStatus(r) !== activeFilters.status) return false;
    return true;
  }), [entitlements, activeFilters]);

  const access = useMemo(() => (org ? computeAccessWindow(org) : null), [org]);

  const accessMismatch = useMemo(() => {
    if (!access || access.isPaidOverride) return null;
    const activeRows = entitlements.filter((r) => rowStatus(r) === 'active');
    if (activeRows.length === 0) return null;
    if (!access.isActive) return 'lapsed';
    if (access.activeUntil && activeRows.some((r) => r.expires_at && new Date(r.expires_at) > access.activeUntil)) return 'short';
    return null;
  }, [access, entitlements]);

  const remainingUsersUntilNextBand = useMemo(
    () => computeRemainingUsersUntilNextBand(seat),
    [seat],
  );

  const frameworkAccessRows = useMemo(() => (
    [...frameworks]
      .map((fw) => {
        const availability = frameworkAvailability(fw);
        const isFree = availability.length === 0 || availability.includes(ESSENTIALS_TIER);
        const locked = isFrameworkLocked(fw, effectiveTiers);
        const paidTiers = isFree
          ? [ESSENTIALS_TIER]
          : (availability.filter((t) => t !== ESSENTIALS_TIER).length
            ? availability.filter((t) => t !== ESSENTIALS_TIER)
            : requiredTiersFor(fw));
        const accessTier = isFree
          ? ESSENTIALS_TIER
          : availability.find((t) => effectiveTiers.includes(t)) || null;
        return {
          id: fw.id,
          name: fw.name || fw.name_acronym || '—',
          entitlementTiers: paidTiers,
          unlocked: !locked,
          accessTier,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name))
  ), [frameworks, effectiveTiers]);

  const frameworkAccessGroups = useMemo(
    () => buildFrameworkAccessGroups(frameworkAccessRows),
    [frameworkAccessRows],
  );

  const handleApplyFilter = useCallback((filter) => {
    setActiveFilters((prev) => {
      if (prev[filter.field] === filter.value) {
        const next = { ...prev };
        delete next[filter.field];
        return next;
      }
      return { ...prev, [filter.field]: filter.value };
    });
  }, []);

  const handleResync = async () => {
    if (!org?.id) return;
    setResyncing(true);
    try {
      await AdminResyncEntitlements(org.id);
      toast.success('Resynced from Stripe');
      await load();
      onOrgChanged && onOrgChanged();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Resync failed');
    } finally {
      setResyncing(false);
    }
  };

  const startAccessEdit = () => {
    setAccessDays(Number(org?.account_expiration_day ?? 14));
    setAccessStart(toDateInput(org?.current_subscription_start_date || org?.created_at));
    setEssentialsCustomDeal(!!org?.essentials_custom_deal);
    setPrepaidAmount(org?.essentials_prepaid_amount_cents != null ? String(org.essentials_prepaid_amount_cents / 100) : '');
    setPrepaidCurrency(org?.essentials_prepaid_currency || 'sgd');
    setPrepaidPeriod(org?.essentials_prepaid_period || 'year');
    setPrepaidNote(org?.essentials_prepaid_note || '');
    setAccessEditing(true);
  };

  const handleSaveAccess = async () => {
    if (!org?.id) return;
    if (essentialsCustomDeal && (!prepaidAmount || Number(prepaidAmount) <= 0)) {
      toast.error('Prepaid Essentials amount is required when custom deal is enabled');
      return;
    }
    setSavingAccess(true);
    try {
      const start = accessStart ? new Date(accessStart).toISOString() : org.current_subscription_start_date;
      const payload = {
        ...org,
        account_expiration_day: Number(accessDays),
        current_subscription_start_date: start,
        essentials_custom_deal: essentialsCustomDeal,
        essentials_prepaid_amount_cents: essentialsCustomDeal ? Math.round(Number(prepaidAmount) * 100) : null,
        essentials_prepaid_currency: essentialsCustomDeal ? prepaidCurrency : null,
        essentials_prepaid_period: essentialsCustomDeal ? prepaidPeriod : null,
        essentials_prepaid_note: essentialsCustomDeal ? (prepaidNote || null) : null,
      };
      await UpdateOrganization(org.id, JSON.stringify(payload));
      toast.success('Platform access updated');
      setAccessEditing(false);
      onOrgChanged && onOrgChanged({ account_expiration_day: Number(accessDays), current_subscription_start_date: start });
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to update platform access');
    } finally {
      setSavingAccess(false);
    }
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await RevokeEntitlement(revokeTarget.id);
      toast.success('Revoked');
      await load();
      onOrgChanged && onOrgChanged();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Revoke failed');
    } finally {
      setRevokeTarget(null);
    }
  };

  const columns = useMemo(() => [
    {
      label: 'Tier',
      field: 'tier',
      minWidth: 140,
      render: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151' }}>{row.tier}</Typography>
          {row.notes && (
            <Tooltip title={row.notes}>
              <StickyNote2OutlinedIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
            </Tooltip>
          )}
        </Stack>
      ),
    },
    {
      label: 'Source',
      field: 'source',
      minWidth: 90,
      render: (row) => <Chip size="small" label={formatSourceLabel(row.source)} />,
    },
    {
      label: 'Status',
      field: 'status',
      minWidth: 120,
      render: (row) => {
        const st = rowStatus(row);
        if (st === 'pending') {
          return (
            <Chip size="small" color="warning" variant="outlined"
              label={`Pending (starts ${new Date(row.starts_at).toLocaleDateString()})`} />
          );
        }
        return (
          <Chip size="small" variant="outlined"
            color={st === 'active' ? 'success' : st === 'expired' ? 'default' : 'error'}
            label={formatStatusLabel(st)} />
        );
      },
    },
    {
      label: 'Granted On',
      field: 'created_at',
      minWidth: 110,
      render: (row) => (
        <Typography variant="body2" color="text.secondary">
          {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
        </Typography>
      ),
    },
    {
      label: 'Granted By',
      field: 'created_by',
      minWidth: 200,
      render: (row) => {
        const label = formatGrantedBy(row.created_by, grantedByLookup);
        return (
          <Tooltip title={label}>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8rem',
                color: '#374151',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 220,
              }}
            >
              {label}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      label: 'Ref',
      field: 'ref',
      minWidth: 90,
      render: (row) => {
        const ref = row.stripe_subscription_id || row.entitlement_code_id || '—';
        return (
          <Tooltip title={ref}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#6B7280' }}>
              {truncate(ref, 12)}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      label: 'Expires',
      field: 'expires_at',
      minWidth: 100,
      render: (row) => (
        <Typography variant="body2" color="text.secondary">
          {row.expires_at ? new Date(row.expires_at).toLocaleDateString() : '—'}
        </Typography>
      ),
    },
  ], [grantedByLookup]);

  const actions = useMemo(() => [
    {
      label: 'Revoke',
      onClick: (row) => setRevokeTarget(row),
      condition: (row) => {
        const st = rowStatus(row);
        return st !== 'revoked' && st !== 'expired';
      },
    },
  ], []);

  if (!org) return null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, mb: 2 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <StandardCollapsibleSection title="Access Overview" icon={InsightsOutlinedIcon}>
        {seat && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
            <HighlightStatCard
              icon={GroupsOutlinedIcon}
              label="Staff (Billed)"
              value={seat.staff_count}
              bg="#F9FAFB"
              iconColor="#1D3DFC"
            />
            <HighlightStatCard
              icon={StorefrontOutlinedIcon}
              label="Vendors"
              value={seat.vendor_count}
              sub="counter only"
              bg="#F5F3FF"
              iconColor="#7C3AED"
            />
            <HighlightStatCard
              icon={PersonOutlineOutlinedIcon}
              label="Guests"
              value={seat.guest_count}
              sub="counter only"
              bg="#FFFBEB"
              iconColor="#D97706"
            />
            <HighlightStatCard
              icon={EventSeatOutlinedIcon}
              label="Included Seats"
              value={seat.included_seats}
              bg="#F0FDF4"
              iconColor="#16A34A"
            />
            <HighlightStatCard
              icon={PersonAddAltOutlinedIcon}
              label="Remaining Users"
              value={remainingUsersUntilNextBand}
              sub={remainingUsersUntilNextBand === 0 ? 'at capacity — next user needs band' : 'until next seat band'}
              bg="#ECFEFF"
              iconColor="#0891B2"
            />
            <HighlightStatCard
              icon={ViewModuleOutlinedIcon}
              label="Seat Bands Billed"
              value={seat.seat_band}
              sub="× 50 users"
              bg="#EFF6FF"
              iconColor="#2563EB"
            />
            {seat.seat_subscription_id && (
              <HighlightStatCard
                icon={SubscriptionsOutlinedIcon}
                label="Seat Subscription"
                value={`qty ${seat.seat_subscription_quantity}`}
                sub={seat.seat_subscription_id}
                bg="#FDF4FF"
                iconColor="#9333EA"
              />
            )}
          </Box>
        )}

          <Box
            sx={{
              mt: 2,
              border: '1px solid #E5E7EB',
              borderRadius: '10px',
              bgcolor: '#FAFAFA',
              p: 2,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>Platform Access</Typography>
                {access?.isPaidOverride ? (
                  <Chip size="small" color="success" label="Paid Client (Override)" />
                ) : access?.isActive ? (
                  <Chip size="small" color="success" variant="outlined" label={`Active until ${access.activeUntil ? access.activeUntil.toLocaleDateString() : '—'}`} />
                ) : (
                  <Chip size="small" color="error" variant="outlined" label={access?.activeUntil ? `Expired ${access.activeUntil.toLocaleDateString()}` : 'No Access Window'} />
                )}
              </Stack>
              {!accessEditing && (
                <Button size="small" className="global-text-button" onClick={startAccessEdit}>Edit Access</Button>
              )}
            </Stack>

            {accessEditing && (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <TextField
                  select size="small" label="Access Window" value={accessDays}
                  onChange={(e) => setAccessDays(Number(e.target.value))} sx={{ minWidth: 240 }}
                >
                  {ACCESS_PRESETS.map((p) => <MenuItem key={p.days} value={p.days}>{p.label}</MenuItem>)}
                </TextField>
                <TextField
                  size="small" type="date" label="Start Date" InputLabelProps={{ shrink: true }}
                  value={accessStart} onChange={(e) => setAccessStart(e.target.value)} sx={{ width: 180 }}
                />
                <Button variant="contained" className="global-primary-button" size="small" onClick={handleSaveAccess} disabled={savingAccess}
                  startIcon={savingAccess ? <CircularProgress size={14} color="inherit" /> : null}>
                  Save
                </Button>
                <Button size="small" className="global-text-button" onClick={() => setAccessEditing(false)} disabled={savingAccess}>Cancel</Button>
              </Stack>
            )}

            {accessMismatch === 'lapsed' && (
              <Alert severity="warning" sx={{ mb: 1.5, py: 0 }}>
                This org holds an active tier but its platform access has lapsed — users can't log in to use it. Extend access above.
              </Alert>
            )}
            {accessMismatch === 'short' && (
              <Alert severity="warning" sx={{ mb: 1.5, py: 0 }}>
                A granted tier outlives the platform-access window (ends {access?.activeUntil?.toLocaleDateString()}). The org can't use it past then — extend access above.
              </Alert>
            )}

            {(access?.isPaidOverride || essentialsCreditUntil) && (
              <Alert severity="info" sx={{ mb: 1.5, py: 0 }}>
                Essentials top-up credit expected while platform access is paid.
                {essentialsCreditUntil && (
                  <> Stripe credit until {new Date(essentialsCreditUntil).toLocaleDateString()}.</>
                )}
              </Alert>
            )}

            {accessEditing && (
              <Stack spacing={1.5} sx={{ mb: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    label={essentialsCustomDeal ? 'Custom Essentials deal' : 'Catalog Essentials credit'}
                    color={essentialsCustomDeal ? 'warning' : 'default'}
                    onClick={() => setEssentialsCustomDeal((v) => !v)}
                    sx={{ cursor: 'pointer' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Toggle for negotiated offline Essentials rates (Policy E)
                  </Typography>
                </Stack>
                {essentialsCustomDeal && (
                  <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                    <TextField
                      size="small" label="Prepaid amount" type="number"
                      value={prepaidAmount} onChange={(e) => setPrepaidAmount(e.target.value)} sx={{ width: 140 }}
                    />
                    <TextField
                      select size="small" label="Currency" value={prepaidCurrency}
                      onChange={(e) => setPrepaidCurrency(e.target.value)} sx={{ width: 100 }}
                    >
                      <MenuItem value="sgd">SGD</MenuItem>
                      <MenuItem value="aud">AUD</MenuItem>
                    </TextField>
                    <TextField
                      select size="small" label="Prepaid period" value={prepaidPeriod}
                      onChange={(e) => setPrepaidPeriod(e.target.value)} sx={{ width: 140 }}
                    >
                      <MenuItem value="month">Month</MenuItem>
                      <MenuItem value="quarter">Quarter</MenuItem>
                      <MenuItem value="year">Year</MenuItem>
                    </TextField>
                    <TextField
                      size="small" label="Note" value={prepaidNote}
                      onChange={(e) => setPrepaidNote(e.target.value)} sx={{ minWidth: 200, flex: 1 }}
                    />
                  </Stack>
                )}
              </Stack>
            )}

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>Effective Framework Access</Typography>
              <Tooltip title={INHERITANCE_NOTE}>
                <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help' }} />
              </Tooltip>
            </Stack>

            <Typography variant="caption" sx={{ display: 'block', color: '#6B7280', mb: 1 }}>
              Entitlement Tiers
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
              {activeEntitlementTiers.map((tier) => (
                <Chip
                  key={tier}
                  size="small"
                  label={tier}
                  variant={tier === ESSENTIALS_TIER ? 'outlined' : 'filled'}
                  color="success"
                />
              ))}
            </Box>

            <Typography variant="caption" sx={{ display: 'block', color: '#6B7280', mb: 1 }}>
              Frameworks
            </Typography>
            {frameworksLoading ? (
              <CircularProgress size={20} />
            ) : frameworkAccessRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No frameworks found.</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: '#fff', maxHeight: 360 }}>
                <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: '38%' }}>Framework</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '62%' }}>Requires</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {frameworkAccessGroups.map((group) => (
                      <React.Fragment key={group.label}>
                        <TableRow sx={{ bgcolor: group.isLocked ? '#FEF2F2' : '#F3F4F6' }}>
                          <TableCell colSpan={2} sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1 }}>
                            {group.label}
                            <Typography component="span" sx={{ ml: 1, color: '#6B7280', fontWeight: 500 }}>
                              ({group.rows.length})
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {group.rows.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell
                              sx={{
                                fontSize: '0.8rem',
                                pl: 3,
                                maxWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={row.name}
                            >
                              {row.name}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              {group.isLocked ? (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                  {row.entitlementTiers.map((tier) => (
                                    <Chip key={tier} size="small" variant="outlined" label={tier} sx={{ fontSize: '0.7rem', height: 22 }} />
                                  ))}
                                </Stack>
                              ) : (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
      </StandardCollapsibleSection>

      <StandardCollapsibleSection
        title="Seats & Entitlements"
        icon={CardMembershipOutlinedIcon}
        contentSx={{ px: 0, py: 0 }}
      >
        <StandardListView
          title="Seats & Entitlements"
          hideTitle
          columns={columns}

          rows={visibleEntitlements}
          actions={actions}
          isEdit
          showMoreIcon
          fullHeight={false}
          fillAvailableHeight={false}
          bottomPaddingPx={8}
          embeddedTableMaxHeight={520}
          addButtonLabel="Grant to Org"
          onAddButtonClick={() => setGrantOpen(true)}
          customHeaderButtons={(
            <Tooltip title={org.stripe_customer_id ? '' : 'No Stripe customer linked'}>
              <span>
                <Button
                  variant="outlined"
                  className="global-outlined-button"
                  size="small"
                  startIcon={resyncing ? <CircularProgress size={14} /> : <SyncIcon />}
                  onClick={handleResync}
                  disabled={resyncing || !org.stripe_customer_id}
                  sx={{ mr: 1 }}
                >
                  Resync from Stripe
                </Button>
              </span>
            </Tooltip>
          )}
          quickFilterComponent={(
            <EntitlementsQuickFilters
              entitlements={entitlements}
              rowStatus={rowStatus}
              activeFilters={activeFilters}
              onApplyFilter={handleApplyFilter}
            />
          )}
          enableQuickFilter
        />
      </StandardCollapsibleSection>

      <GrantEntitlementDialog
        open={grantOpen}
        org={org}
        onClose={() => setGrantOpen(false)}
        onGranted={async () => { await load(); onOrgChanged && onOrgChanged(); }}
      />
      <ConfirmDialog
        open={!!revokeTarget}
        title="Revoke Entitlement"
        onClose={() => setRevokeTarget(null)}
        onConfirm={confirmRevoke}
        confirmLabel="Revoke"
      >
        Revoke <strong>{revokeTarget?.tier}</strong> for <strong>{org?.organization_name}</strong>? This cannot be undone.
      </ConfirmDialog>
    </Box>
  );
}

function HighlightStatCard({ icon: Icon, label, value, sub, bg, iconColor }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.25,
        borderRadius: '10px',
        backgroundColor: bg,
        border: '1px solid #E5E7EB',
        minWidth: 130,
        flex: '0 0 auto',
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          backgroundColor: `${iconColor}18`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 17, color: iconColor }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 17, color: '#111827', lineHeight: 1 }}>
          {value ?? '—'}
        </Typography>
        <Typography sx={{ fontSize: 11, color: '#6B7280', mt: 0.25, whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
        {sub ? (
          <Typography sx={{ fontSize: 10, color: '#9CA3AF', mt: 0.15, wordBreak: 'break-all' }}>
            {sub}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}
